/**
 * Shopify Section Rendering helper
 * Fetches and updates specific sections without full page reload using Shopify's section rendering API
 */

import { globalState } from './utils'

interface SectionConfig {
  sectionId: string
  fetchSelectors: string[]
  targetSelectors: string[]
  onSectionLoad?: (elements: HTMLElement[]) => void
  baseUrl?: string
  sectionIdFallbacks?: string[]
}

interface SectionResponse {
  [key: string]: string
}

export class ShopifySectionHelper {
  private static async fetchSectionHtml(
    sectionIds: string | string[],
    baseUrl: string = globalState.routes.root
  ): Promise<SectionResponse> {
    try {
      const sectionParam = Array.isArray(sectionIds) ? sectionIds.join(',') : sectionIds
      const separator = baseUrl.includes('?') ? '&' : '?'
      const response = await fetch(`${baseUrl}${separator}sections=${sectionParam}`)
      if (!response.ok) throw new Error(`Failed to fetch sections: ${sectionParam}`)
      return await response.json()
    } catch (error) {
      console.error(`Error fetching sections ${sectionIds}:`, error)
      throw error
    }
  }

  private static createDOMFromString(htmlString: string): DocumentFragment {
    const template = document.createElement('template')
    template.innerHTML = htmlString.trim()
    return template.content
  }

  /**
   * Fetches a full page and swaps matching fragments into the current DOM.
   *
   * Unlike the Section Rendering API (`?sections=`), a full-page fetch always
   * renders the section with the merchant's saved instance settings (so
   * toggled-off blocks stay hidden) and avoids any section-id ambiguity.
   *
   * @param config url + fetch/target selector pairs
   * @returns Promise<HTMLElement[]> the fragments pulled from the fetched page
   */
  static async fetchAndUpdatePage(config: {
    url: string
    fetchSelectors: string[]
    targetSelectors: string[]
    onSectionLoad?: (elements: HTMLElement[]) => void
  }): Promise<HTMLElement[]> {
    const { url, fetchSelectors, targetSelectors, onSectionLoad } = config

    if (fetchSelectors.length !== targetSelectors.length) {
      throw new Error('The number of fetch selectors must match the number of target selectors')
    }

    try {
      const response = await fetch(url)
      if (!response.ok) throw new Error(`Failed to fetch page: ${url}`)
      const html = await response.text()
      const doc = new DOMParser().parseFromString(html, 'text/html')
      const fetchedElements: HTMLElement[] = []

      fetchSelectors.forEach((fetchSelector, index) => {
        const targetSelector = targetSelectors[index]
        const elementToFetch = doc.querySelector(fetchSelector) as HTMLElement
        if (!elementToFetch) {
          console.warn(`Could not find element with selector "${fetchSelector}" in ${url}`)
          return
        }

        const targetElement = document.querySelector(targetSelector) as HTMLElement
        if (!targetElement) {
          console.warn(`Could not find target element with selector "${targetSelector}"`)
          return
        }

        targetElement.replaceWith(elementToFetch.cloneNode(true))
        fetchedElements.push(elementToFetch)
      })

      if (onSectionLoad) {
        onSectionLoad(fetchedElements)
      }

      return fetchedElements
    } catch (error) {
      console.error('Error in fetchAndUpdatePage:', error)
      throw error
    }
  }

  /**
   * Fetches a section by ID and updates the DOM based on provided selectors
   * @param config Configuration object for fetching and updating sections
   * @param sectionResponse Optional pre-fetched section response
   * @returns Promise<HTMLElement[]> Array of elements that were fetched from the section
   */
  static async fetchAndUpdateSection(
    config: SectionConfig,
    sectionResponse?: SectionResponse
  ): Promise<HTMLElement[]> {
    const { sectionId, fetchSelectors, targetSelectors, onSectionLoad, baseUrl, sectionIdFallbacks = [] } = config

    if (fetchSelectors.length !== targetSelectors.length) {
      throw new Error('The number of fetch selectors must match the number of target selectors')
    }

    try {
      const candidateIds = [sectionId, ...sectionIdFallbacks]
      const response = sectionResponse || await this.fetchSectionHtml(candidateIds, baseUrl)
      const resolvedId = candidateIds.find((id) => response[id])
      const sectionHtml = resolvedId ? response[resolvedId] : undefined
      if (!sectionHtml) {
        throw new Error(`Section ${sectionId} not found`)
      }
      const sectionFragment = this.createDOMFromString(sectionHtml)
      const fetchedElements: HTMLElement[] = []

      // Process each selector pair
      fetchSelectors.forEach((fetchSelector, index) => {
        const targetSelector = targetSelectors[index]
        // Find the element to fetch from the section
        const elementToFetch = sectionFragment.querySelector(fetchSelector) as HTMLElement
        if (!elementToFetch) {
          console.warn(`Could not find element with selector "${fetchSelector}" in section ${sectionId}`)
          return
        }

        // Find the target element to replace
        const targetElement = document.querySelector(targetSelector) as HTMLElement
        if (!targetElement) {
          console.warn(`Could not find target element with selector "${targetSelector}"`)
          return
        }

        // Replace the target element with the fetched element
        targetElement.replaceWith(elementToFetch.cloneNode(true))
        fetchedElements.push(elementToFetch)
      })

      // Call the onSectionLoad callback if provided
      if (onSectionLoad) {
        onSectionLoad(fetchedElements)
      }

      return fetchedElements
    } catch (error) {
      console.error('Error in fetchAndUpdateSection:', error)
      throw error
    }
  }

  /**
   * Fetch multiple sections and update the DOM with a single request
   * @param configs Array of section configurations
   * @returns Promise<Map<string, HTMLElement[]>>
   */
  static async fetchAndUpdateMultipleSections(configs: SectionConfig[]): Promise<Map<string, HTMLElement[]>> {
    const results = new Map<string, HTMLElement[]>()

    // Get all unique section IDs
    const sectionIds = [...new Set(configs.map(config => config.sectionId))]

    // Fetch all sections in a single request
    const sectionsResponse = await this.fetchSectionHtml(sectionIds)

    // Process each config with the pre-fetched response
    await Promise.all(
      configs.map(async (config) => {
        const elements = await this.fetchAndUpdateSection(config, sectionsResponse)
        results.set(config.sectionId, elements)
      })
    )

    return results
  }
}
